import { IElementAutoLayoutService, IServiceDelegates } from './element-auto-layout-service.interface';
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { IOriginalSize } from '@src/slide/domain/ui-model/element-modeles/property/image-size/image-size.interface';
import { IFrame } from '@src/slide/domain/ui-model/element-modeles/property/frame/frame.interface';

export enum LINE_NUMBER {
    SINGLE = 1,
    DOUBLE,
    MUITI,
}

const DEFAULT_GAP = 5;

const SCALE = 0.95;

interface ElementSizePosInfo {
    frame: IFrame;
    elementModel: IElementBaseModel;
    originalSize: IOriginalSize;
    adjustedSize: boolean;
}

/**
 * 自动布局相关服务
 */
export default class ElementAutoLayoutService implements IElementAutoLayoutService {
    // 同时修改多个元素的frame时，可能会影响到组合/连接线，该frame存储所有要改的元素的frame信息
    private frameMap = new Map();
    private tilingType: LINE_NUMBER;
    private services: IServiceDelegates;

    public constructor(services: IServiceDelegates) {
        this.services = services;
    }

    public layout(elements: IElementBaseModel[], slideWidth: number, slideHeight: number, tilingType: LINE_NUMBER) {
        this.tilingType = tilingType;
        const maxLineHeight = this.getMaxLineHeight(slideHeight);
        const elementLayoutArr = this.getElementLayoutArr(elements);
        elementLayoutArr.forEach((elementRow, lineIndex) => {
            if (!elementRow.length) return;
            const totalElementWidth = elementRow.reduce((total, element) => {
                const eleWidth = element.props.style.width + total;
                return eleWidth;
            }, 0);
            const availableWidth = (slideWidth * SCALE) - ((elementRow.length - 1) * DEFAULT_GAP);
            const elementsInfo = this.getElementsInfo(elementRow, totalElementWidth, availableWidth);
            this.adjustElementsSize(elementsInfo, availableWidth, maxLineHeight);
            this.adjustElementsPos(elementsInfo, availableWidth, maxLineHeight, lineIndex, slideWidth, slideHeight);
            this.updateElementsFrame(elementsInfo);
        });
        this.frameMap.clear();
    }

    public getElementsInfoAfterLayout(
        elements: IElementBaseModel[],
        slideWidth: number,
        slideHeight: number,
        tilingType: LINE_NUMBER,
    ) {
        this.tilingType = tilingType;
        const maxLineHeight = this.getMaxLineHeight(slideHeight);
        const elementLayoutArr = this.getElementLayoutArr(elements);
        const elementsInfoArr: any = [];
        elementLayoutArr.forEach((elementRow, lineIndex) => {
            if (!elementRow.length) return;
            const totalElementWidth = elementRow.reduce((total, element) => {
                const eleWidth = element.props.style.width + total;
                return eleWidth;
            }, 0);
            const availableWidth = (slideWidth * SCALE) - ((elementRow.length - 1) * DEFAULT_GAP);
            const elementsInfo = this.getElementsInfo(elementRow, totalElementWidth, availableWidth);
            this.adjustElementsSize(elementsInfo, availableWidth, maxLineHeight);
            this.adjustElementsPos(elementsInfo, availableWidth, maxLineHeight, lineIndex, slideWidth, slideHeight);
            elementsInfoArr.push(elementsInfo);
        });
        this.frameMap.clear();
        return elementsInfoArr;
    }

    /**
     * 针对单行元素调整元素宽高，需要循环两次，第一次循环将高度超限的元素宽高调整到limit
     * 由于超限的元素高度变小，则其宽度也会变小
     * 该情况下，本行中留给其他元素的宽度余额变大，则需要对剩余的元素再次循环以调大剩余元素宽度，宽度变大的同时高度也会变大
     * 递归地对当前元素数组进行该过程，所有元素都被调整到limit或本次过程中没有元素超高
     */
    private adjustElementsSize = (
        elementsInfo: ElementSizePosInfo[],
        availableWidth: number,
        maxLineHeight: number,
    ) => {
        let currentAvailableWidth = availableWidth;
        let needAdjustedAgain = false;
        elementsInfo.forEach((elementInfo) => {
            if (elementInfo.adjustedSize) return;
            if (elementInfo.frame.height > maxLineHeight) {
                const { width, height } = elementInfo.originalSize;
                elementInfo.frame.height = maxLineHeight;
                elementInfo.frame.width = (width / height) * maxLineHeight;
                currentAvailableWidth = currentAvailableWidth - elementInfo.frame.width;
                needAdjustedAgain = true;
                elementInfo.adjustedSize = true;
            }
        });
        const currentTotalEleWidth = elementsInfo.reduce((total, elementInfo) => {
            if (elementInfo.adjustedSize) return total;
            const eleWidth = elementInfo.originalSize.width + total;
            return eleWidth;
        }, 0);
        if (needAdjustedAgain) {
            elementsInfo.forEach((elementInfo) => {
                if (elementInfo.adjustedSize) return;
                const newWidth = (elementInfo.originalSize.width / currentTotalEleWidth) * currentAvailableWidth;
                const newHeight = (elementInfo.originalSize.height / elementInfo.originalSize.width) * newWidth;
                elementInfo.frame.width = newWidth;
                elementInfo.frame.height = newHeight;
            });
            this.adjustElementsSize(elementsInfo, currentAvailableWidth, maxLineHeight);
        }
    };

    private updateElementsFrame = (elementsInfo: ElementSizePosInfo[]) => {
        const updateModelArr: IElementBaseModel[] = [];
        const updateFrameArr: IFrame[] = [];
        elementsInfo.forEach((elementInfo) => {
            updateModelArr.push(elementInfo.elementModel as IElementBaseModel);
            const modelFrame = (elementInfo.elementModel as IElementBaseModel).getFrame?.().getFrameInfo();
            updateFrameArr.push({ ...modelFrame, ...elementInfo.frame });
        });
        this.services.selectionService.currentSlide?.getBatchUpdateFrame()
            .batchUpdateFrame({ frame: updateFrameArr, updateModelArr });
    };

    private getElementsInfo = (
        elementRow: IElementBaseModel[],
        totalElementWidth: number,
        availableWidth: number,
    ) => elementRow.map((element) => {
        const { width, height } = element.props.style;
        const adjustedSize = false;
        const newWidth = (width / totalElementWidth) * availableWidth;
        const newHeight = (height / width) * newWidth;
        return {
            elementModel: element,
            frame: {
                width: newWidth,
                height: newHeight,
                left: 0,
                top: 0,
            },
            originalSize: {
                width,
                height,
            },
            adjustedSize,
        };
    });

    /**
     * 调整元素位置
     */
    private adjustElementsPos = (
        elementsInfo: ElementSizePosInfo[],
        availableWidth: number,
        maxLineHeight: number,
        lineIndex: number,
        slideWidth: number,
        slideHeight: number,
    ) => {
        const totalElementWidth = elementsInfo.reduce((total, elementInfo) => {
            const eleWidth = elementInfo.frame.width + total;
            return eleWidth;
        }, 0);
        let startLeft = slideWidth * ((1 - SCALE) / 2);
        const startTop = (slideHeight * ((1 - SCALE) / 2)) + (lineIndex * (maxLineHeight + DEFAULT_GAP));
        elementsInfo.forEach((elementInfo) => {
            const occupyWidth = (elementInfo.frame.width / totalElementWidth) * availableWidth;
            const marginLeft = (occupyWidth - elementInfo.frame.width) / 2;
            const marginTop = (maxLineHeight - elementInfo.frame.height) / 2;
            elementInfo.frame.left = startLeft + marginLeft;
            elementInfo.frame.top = startTop + marginTop;
            startLeft += occupyWidth + DEFAULT_GAP;
        });
    };


    /**
     * 获取元素布局的二维数组，单行布局为[[]],双行布局为[[],[]]以此类推
     */
    private getElementLayoutArr = (eleArr: IElementBaseModel[]): IElementBaseModel[][] => {
        const layoutArr: IElementBaseModel[][] = new Array(this.tilingType).fill('')
            .map(() => []);
        const eleNumInLastCol = eleArr.length % this.tilingType || this.tilingType;
        const colNum = Math.ceil(eleArr.length / this.tilingType);
        let rowIndex = 0;
        eleArr.forEach((elementModel) => {
            layoutArr[rowIndex].push(elementModel);
            const elementNumInRow = layoutArr[rowIndex].length;
            if (rowIndex < eleNumInLastCol) {
                if (elementNumInRow === colNum) {
                    rowIndex += 1;
                }
            } else {
                if (elementNumInRow === colNum - 1) {
                    rowIndex += 1;
                }
            }
        });
        return layoutArr;
    };

    /**
     * 获取单行的高度限制
     */
    private getMaxLineHeight = (slideHeight: number) => (
        (slideHeight / this.tilingType) * SCALE) - ((this.tilingType - 1) * DEFAULT_GAP);
}
